// frontend/src/components/CRRibbon.tsx
// Горизонтальная лента из 5 heat-coded ячеек с конверсиями между шагами.
// Цвет фона по delta vs предыдущий период: good (green) / bad (red) / mid (amber).
import { METRICS, type MetricId } from "../charts/METRICS.js";

export interface CRRibbonStep {
  id: MetricId;
  current: number | null;
  previous: number | null;
}

export interface CRRibbonProps {
  steps: CRRibbonStep[];
}

export function CRRibbon({ steps }: CRRibbonProps) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
        gap: 0,
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "var(--shadow-card)",
        marginBottom: 24,
      }}
    >
      {steps.map((s, i) => (
        <CRCell key={s.id} step={s} isLast={i === steps.length - 1} />
      ))}
    </div>
  );
}

function CRCell({ step, isLast }: { step: CRRibbonStep; isLast: boolean }) {
  const meta = METRICS[step.id];
  const delta = computeDelta(step.current, step.previous);
  const heat = classifyHeat(step.id, step.current, delta);
  const arrow = delta == null ? "—" : delta > 0 ? "▲" : delta < 0 ? "▼" : "—";
  const deltaPct = delta == null
    ? "—"
    : `${(Math.abs(delta) * 100).toFixed(1).replace(".", ",")}%`;
  const invert = ("invertGoodSign" in meta) && meta.invertGoodSign;
  const isGood = delta == null ? null : (delta >= 0) === !invert;
  const deltaColor = isGood == null
    ? "var(--text-muted)"
    : isGood
      ? "var(--status-good-fg)"
      : "var(--status-bad-fg)";

  const bg = (() => {
    switch (heat) {
      case "good":
        return "linear-gradient(180deg, #ECFDF5 0%, var(--surface-card) 100%)";
      case "bad":
        return "linear-gradient(180deg, #FEF2F2 0%, var(--surface-card) 100%)";
      case "mid":
        return "linear-gradient(180deg, #FEF3C7 0%, var(--surface-card) 100%)";
      default:
        return "var(--surface-card)";
    }
  })();

  return (
    <div
      style={{
        padding: "14px 16px",
        borderRight: isLast ? "none" : "1px solid var(--border-default)",
        textAlign: "center",
        background: bg,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
        {shortLabel(step.id, meta.label)}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          marginTop: 4,
          color: "var(--text-primary)",
        }}
      >
        {meta.format(step.current)}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: deltaColor,
          marginTop: 4,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {arrow} {deltaPct}
      </div>
    </div>
  );
}

function computeDelta(current: number | null, previous: number | null): number | null {
  if (current == null || previous == null || previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

// Абсолютные пороги для heat-цвета по уровню CR. Используются когда delta null
// (нет previous-периода), чтобы карточки всё равно были раскрашены.
const ABSOLUTE_THRESHOLDS: Record<string, { good: number; mid: number }> = {
  cr_click_to_visit:     { good: 0.70, mid: 0.50 },
  cr_visit_to_basket:    { good: 0.05, mid: 0.02 },
  cr_basket_to_checkout: { good: 0.50, mid: 0.30 },
  cr_checkout_to_order:  { good: 0.70, mid: 0.50 },
  cr_order_to_buyout:    { good: 0.60, mid: 0.40 },
};

function classifyHeat(
  id: string,
  current: number | null,
  delta: number | null,
): "good" | "mid" | "bad" | null {
  // 1) если есть delta — heat по тренду vs прошлый период
  if (delta != null) {
    const abs = Math.abs(delta);
    if (abs < 0.02) return "mid";
    return delta > 0 ? "good" : "bad";
  }
  // 2) fallback — абсолютный порог по уровню метрики
  if (current == null) return null;
  const t = ABSOLUTE_THRESHOLDS[id];
  if (!t) return "mid";
  if (current >= t.good) return "good";
  if (current >= t.mid) return "mid";
  return "bad";
}

function shortLabel(id: MetricId, fallback: string): string {
  // на ribbon хочется короче, чем «CR клик→визит»
  switch (id) {
    case "cr_click_to_visit":
      return "Клик→Визит";
    case "cr_visit_to_basket":
      return "Визит→Корзина";
    case "cr_basket_to_checkout":
      return "Корзина→Чекаут";
    case "cr_checkout_to_order":
      return "Чекаут→Заказ";
    case "cr_order_to_buyout":
      return "Заказ→Выкуп";
    default:
      return fallback;
  }
}
